/**
 * Puntero de ORGANIZACIÓN ACTIVA (multi-org switcher — pieza 1 de
 * `infra/guides/MULTI_ORG_SWITCHER.md`).
 *
 * Un mismo usuario opera N academias (organization) y cambia entre ellas con un
 * switcher. El puntero es SOLO preferencia: se persiste como mapa
 * `userId → orgId` en localStorage (multi-cuenta: cada cuenta recuerda su org
 * activa) y se VALIDA contra las orgs presentes en local, auto-sanándose a la
 * primera si quedó viejo/inválido.
 *
 * Es un signal a nivel de MÓDULO (no de componente): un `setActiveOrg` re-scopea
 * reactivamente TODAS las instancias del hook `use-active-org` (header + páginas)
 * sin recargar.
 *
 * Aislamiento (pieza 5, CRÍTICO): al cambiar de org incrementamos un EPOCH
 * explícito para que los guards de entidades deep-linkeadas descarten un puntero
 * que ya no pertenece a la org activa. Ver `use-active-org.ts`.
 */

import { createSignal } from "solid-js";

const STORAGE_KEY = "space.activeOrg"; // mapa userId → orgId (JSON)

type ActiveOrgMap = Record<string, string>;

function readMap(): ActiveOrgMap {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return {};
    }
    // Copia defensiva quedándonos solo con las entradas string→string válidas.
    const out: ActiveOrgMap = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === "string") {
        out[k] = v;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function writeMap(map: ActiveOrgMap): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // storage unavailable — best-effort only.
  }
}

// Signal de módulo: el mapa completo userId→orgId. Un cambio re-scopea todo.
const [orgMap, setOrgMap] = createSignal<ActiveOrgMap>(readMap());

// Epoch de cambio explícito (pieza 5): se incrementa SOLO en un switch de org
// del usuario, nunca en el boot/auto-saneo. Los guards de aislamiento lo usan
// para no borrar deep-links durante la hidratación inicial.
const [orgSwitchEpoch, setOrgSwitchEpoch] = createSignal(0);

/** Lee (reactivo) el orgId preferido para un usuario, sin validar. */
function preferredOrgId(userId: string | undefined): string | undefined {
  if (!userId) {
    return undefined;
  }
  return orgMap()[userId];
}

/** Epoch reactivo del último switch explícito de org. */
export function activeOrgEpoch(): number {
  return orgSwitchEpoch();
}

/**
 * Fija la org activa del usuario. `epochBump=true` (default) marca un switch
 * EXPLÍCITO del usuario (dispara guards de aislamiento); el auto-saneo llama con
 * `false` para no disparar los guards durante el boot.
 */
export function setPreferredOrgId(userId: string, orgId: string, epochBump = true): void {
  const next = { ...orgMap(), [userId]: orgId };
  setOrgMap(next);
  writeMap(next);
  if (epochBump) {
    setOrgSwitchEpoch((e) => e + 1);
  }
}

/**
 * Resuelve la org ACTIVA validada contra las orgs presentes. Si el puntero es
 * inválido/viejo, auto-sana a la primera org disponible (sin bump de epoch).
 * Devuelve `undefined` si el usuario no tiene orgs.
 */
export function resolveActiveOrgId(
  userId: string | undefined,
  availableOrgIds: string[],
): string | undefined {
  if (!userId || availableOrgIds.length === 0) {
    return undefined;
  }
  const preferred = preferredOrgId(userId);
  if (preferred && availableOrgIds.includes(preferred)) {
    return preferred;
  }
  // Auto-saneo: puntero viejo o ausente → primera org (sin marcar switch).
  const fallback = availableOrgIds[0];
  if (!fallback) {
    return undefined;
  }
  setPreferredOrgId(userId, fallback, false);
  return fallback;
}
